import { Box, CircularProgress, Stack, Typography } from "@mui/joy";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { theme } from "styles/theme";
import React, { useEffect } from "react";
import {
  BellIconSvg,
  CardIconSvg,
  HeadphoneIconSvg,
  MyProfileIconSvg,
  ShieldIconSvg,
} from "static/image/common/SvgAssets";
import ListButton from "components/common/ListButton";
import { extractHexColor } from "styles/hexOpacity";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import useOpenTask from "hooks/useOpenTask";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import useIsNotificationSettingsAvailable from "../../hooks/useIsNotificationSettingsAvailable";
import useIsSecuritySettingsAvailable from "../../hooks/useIsSecuritySettingsAvailable";

type MenuItem = {
  icon: React.ReactNode;
  text: string;
  taskKey: string;
};

const menuItems: MenuItem[] = [
  { icon: <BellIconSvg />, text: "알림", taskKey: "notification-settings" },
  { icon: <ShieldIconSvg />, text: "앱 잠금", taskKey: "security-settings" },
  {
    icon: <CardIconSvg />,
    text: "결제",
    taskKey: "payment-history",
  },
  {
    icon: <MyProfileIconSvg />,
    text: "계정",
    taskKey: "account",
  },
  {
    icon: <HeadphoneIconSvg />,
    text: "고객센터",
    taskKey: "support",
  },
];

export default function MyPage() {
  const user = useRecoilValue(userAtom);
  const isInappWebview = useIsInappWebview();
  const { isInAppSubscription, expirationAt } = useSubscriptionStatus();
  const isNotificationSettingsAvailable = useIsNotificationSettingsAvailable();
  const isSecuritySettingsAvailable = useIsSecuritySettingsAvailable();

  const notificationSettingsTaskOpener = useOpenTask({
    taskKey: "notification-settings",
  });
  const securitySettingsTaskOpener = useOpenTask({
    taskKey: "security-settings",
  });
  const paymentHistoryTaskOpener = useOpenTask({ taskKey: "payment-history" });
  const accountTaskOpener = useOpenTask({ taskKey: "account" });
  const supportTaskOpener = useOpenTask({ taskKey: "support" });

  const nickname = user?.nickname;
  const dayNumber = user?.dayNumber || 0;
  const backgroundColor = theme.vars.palette.Background.Level1;

  const filteredMenuItems = menuItems.filter((item) => {
    if (item.taskKey === "payment-history") {
      return !(
        isInAppSubscription ||
        expirationAt === null ||
        expirationAt === undefined ||
        user?.isStoreReviewer === true
      );
    }

    if (item.taskKey === "notification-settings") {
      return isNotificationSettingsAvailable;
    }

    if (item.taskKey === "security-settings") {
      return isSecuritySettingsAvailable;
    }

    return true;
  });

  useEffect(() => {
    if (isInappWebview) {
      const topBackground = extractHexColor(backgroundColor);

      if (topBackground) {
        sendMessage(
          JSON.stringify({
            event: "set_safearea_color",
            params: {
              topColor: `${topBackground}`,
            },
          })
        );
      }
    }
  }, []);

  return nickname ? (
    <>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          px: "20px",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            maxWidth: "660px",
            width: "100%",
            mx: "auto",
            px: "4px",
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: `60px`, // 헤더 높이 설정
          }}
        >
          <Typography
            variant={"H3"}
            sx={{
              fontWeight: 700,
              color: theme.vars.palette.Content.Strong,
            }}
          >
            {nickname}
          </Typography>
        </Stack>
      </Stack>
      <ContentTemplateV2>
        <Stack spacing={1}>
          <Stack spacing={1}>
            {filteredMenuItems.map((item, index) => (
              <ListButton
                key={index}
                icon={item.icon}
                text={item.text}
                variant={"Plain"}
                color={"neutral"}
                onClick={() => {
                  if (item.taskKey === "payment-history") {
                    paymentHistoryTaskOpener();
                  } else if (item.taskKey === "account") {
                    accountTaskOpener();
                  } else if (item.taskKey === "support") {
                    supportTaskOpener();
                  } else if (item.taskKey === "notification-settings") {
                    notificationSettingsTaskOpener();
                  } else if (item.taskKey === "security-settings") {
                    securitySettingsTaskOpener();
                  }
                }}
              />
            ))}
          </Stack>
        </Stack>
      </ContentTemplateV2>
    </>
  ) : (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
      }}
    >
      <CircularProgress color="neutral" />
    </Box>
  );
}
